import { FieldError } from "@/components/form/ConfigField";

const optionClass = (checked) =>
    `flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer ${checked ? "surface-soft border-transparent font-bold shadow-[inset_5px_0_0_var(--edi-lime)]" : "border-edi-border bg-edi-surface hover:border-edi-border-strong"}`;

export function toggleMulti(question, current, value) {
    const exclusive = new Set(question.options.filter((o) => o.exclusive).map((o) => o.value));
    const list = current || [];
    if (list.includes(value)) return list.filter((v) => v !== value);
    if (exclusive.has(value)) return [value];
    return [...list.filter((v) => !exclusive.has(v)), value];
}

export const QuestionField = ({ question, number, value, onChange, error }) => {
    const qid = question.id;
    const errId = `question-${qid}-error`;
    const legend = (
        <legend className="font-heading font-bold text-lg mb-3">
            <span className="text-edi-muted mr-1">{number}.</span> {question.text}
            {!question.required && <span className="font-body font-normal text-edi-muted text-base"> (facoltativa)</span>}
        </legend>
    );

    if (question.type === "text") {
        return (
            <div id={`question-${qid}`} tabIndex={-1} className={`rounded-xl p-4 sm:p-5 border-2 ${error ? "border-edi-error" : "border-transparent"}`} data-testid={`question-${qid}`}>
                <label htmlFor={`q-${qid}`} className="font-heading font-bold text-lg block mb-3">
                    <span className="text-edi-muted mr-1">{number}.</span> {question.text}
                    {!question.required && <span className="font-body font-normal text-edi-muted text-base"> (facoltativa)</span>}
                </label>
                <textarea id={`q-${qid}`} className="edi-input min-h-[120px]" maxLength={question.max_length || 1000} value={value || ""} onChange={(e) => onChange(e.target.value)} aria-describedby={`q-${qid}-count`} data-testid={`question-${qid}-textarea`} />
                <p id={`q-${qid}-count`} className="edi-help">{(value || "").length}/{question.max_length || 1000} caratteri</p>
            </div>
        );
    }

    let body;
    if (question.type === "scale") {
        const values = Array.from({ length: question.max - question.min + 1 }, (_, i) => question.min + i);
        body = (
            <>
                <p className="edi-help mb-2" id={`q-${qid}-scale-help`}>Scala da {question.min} ({question.min_label}) a {question.max} ({question.max_label})</p>
                <div className="flex flex-wrap gap-2">
                    {values.map((v) => (
                        <label key={v} className={`${optionClass(value === v)} justify-center min-w-[3.5rem]`}>
                            <input type="radio" name={qid} className="sr-only peer" checked={value === v} onChange={() => onChange(v)} data-testid={`scale-${qid}-${v}`} />
                            <span className="font-bold text-lg">{v}</span>
                        </label>
                    ))}
                </div>
            </>
        );
    } else {
        const multi = question.type === "multi";
        body = (
            <>
                {multi && <p className="edi-help mb-2">Puoi selezionare più risposte</p>}
                <div className="grid gap-2 sm:grid-cols-2">
                    {question.options.map((o) => {
                        const checked = multi ? (value || []).includes(o.value) : value === o.value;
                        return (
                            <label key={o.value} className={optionClass(checked)}>
                                <input
                                    type={multi ? "checkbox" : "radio"}
                                    name={qid}
                                    className="mt-1 h-5 w-5 shrink-0 accent-[var(--edi-primary)]"
                                    checked={checked}
                                    onChange={() => onChange(multi ? toggleMulti(question, value, o.value) : o.value)}
                                    data-testid={`option-${o.value}`}
                                />
                                <span>{o.label}</span>
                            </label>
                        );
                    })}
                </div>
            </>
        );
    }

    return (
        <fieldset
            id={`question-${qid}`}
            tabIndex={-1}
            aria-describedby={error ? errId : undefined}
            aria-invalid={error ? "true" : undefined}
            className={`rounded-xl p-4 sm:p-5 border-2 ${error ? "border-edi-error bg-edi-error-bg" : "border-transparent"}`}
            data-testid={`question-${qid}`}
        >
            {legend}
            {body}
            <FieldError id={errId} error={error} />
        </fieldset>
    );
};
